// =============================================================================
// Origin UI — src/services/leaveApi.ts
// API service for Leave Management endpoints (Phase 6).
// Updated: added managerId parameter to getTeamMembers and getTeamCalendar.
// =============================================================================

import { api } from './apiService';

// ─── Types ───

export interface LeaveBalance {
  leaveTypeId: string;
  leaveTypeCode: string;
  leaveTypeName: string;
  isPaid: boolean;
  balanceHidden: boolean;
  entitlement: number;
  accruedToDate: number;
  carryForward: number;
  taken: number;
  committed: number;
  pending: number;
  available: number;
}

export interface LeaveRequestListItem {
  leaveRequestId: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  leaveTypeCode: string;
  leaveTypeName: string;
  startDate: string;
  endDate: string;
  totalWorkingDays: number;
  status: string;
  createdAt: string;
  availableBalance: number | null;
  entitlement: number | null;
}

export interface LeaveRequestDetail extends LeaveRequestListItem {
  startDateIsHalfDay: boolean;
  startDateHalfDayPeriod: string | null;
  endDateIsHalfDay: boolean;
  endDateHalfDayPeriod: string | null;
  reason: string | null;
  approverId: string | null;
  approverName: string | null;
  approverComments: string | null;
  approvedAt: string | null;
  cancellationReason: string | null;
  cancelledAt: string | null;
  attachmentFileName: string | null;
  updatedAt: string;
}

export interface CreateLeaveRequest {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  startDateIsHalfDay: boolean;
  startDateHalfDayPeriod?: string | null;
  endDateIsHalfDay: boolean;
  endDateHalfDayPeriod?: string | null;
  reason?: string;
}

export interface PublicHoliday {
  publicHolidayId: string;
  officeLocationId: number;
  officeName: string;
  year: number;
  date: string;
  name: string;
  isHalfDay: boolean;
  notes: string | null;
  isActive: boolean;
}

export interface WorkingDaysResponse {
  workingDays: number;
  publicHolidaysInRange: string[];
  weekendsInRange: string[];
}

export interface LeaveType {
  leaveTypeId: string;
  code: string;
  name: string;
  description: string | null;
  isPaid: boolean;
  requiresAttachment: boolean;
  attachmentRequiredAfterDays: number | null;
  allowHalfDay: boolean;
  allowCarryForward: boolean;
  maxCarryForwardDays: number | null;
  carryForwardExpiryMonths: number | null;
  defaultEntitlementDays: number | null;
  maxConsecutiveDays: number | null;
  requiresApproval: boolean;
  sortOrder: number;
  isActive: boolean;
}

export interface TeamCalendarEntry {
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  leaveRequestId: string;
  leaveTypeCode: string;
  leaveTypeName: string;
  startDate: string;
  endDate: string;
  totalWorkingDays: number;
  status: string;
}

export interface TeamMember {
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  departmentName: string;
  officeName: string;
}

export interface LeaveOverlapWarning {
  teamMembersOffCount: number;
  totalTeamSize: number;
  peopleOff: { employeeName: string; leaveTypeName: string; startDate: string; endDate: string }[];
  isWarning: boolean;
  warningMessage: string | null;
}

export interface LeaveBalanceReportItem {
  employeeId: string;
  employeeNumber: string;
  oqtId: string;
  employeeName: string;
  departmentName: string;
  officeName: string;
  balances: LeaveBalance[];
}

export interface LeaveUsageReportItem {
  leaveRequestId: string;
  employeeNumber: string;
  oqtId: string;
  employeeName: string;
  departmentName: string;
  officeName: string;
  leaveTypeCode: string;
  leaveTypeName: string;
  startDate: string;
  endDate: string;
  totalWorkingDays: number;
  status: string;
  createdAt: string;
}

export interface LeaveApprovalDelegation {
  delegationId: string;
  delegatorId: string;
  delegatorName: string | null;
  delegateId: string;
  delegateName: string | null;
  startDate: string;
  endDate: string;
  reason: string | null;
  isActive: boolean;
  createdAt: string;
}

// ─── API ───

export const leaveApi = {
  // Balance
  getBalance: (year?: number) =>
    api.get<LeaveBalance[]>(year ? `/api/leave/balance/${year}` : '/api/leave/balance'),

  // Requests
  getMyRequests: (year?: number, leaveTypeId?: string) => {
    const params = new URLSearchParams();
    if (year) params.set('year', year.toString());
    if (leaveTypeId) params.set('leaveTypeId', leaveTypeId);
    const qs = params.toString();
    return api.get<LeaveRequestListItem[]>(`/api/leave/requests${qs ? '?' + qs : ''}`);
  },

  getRequest: (id: string) =>
    api.get<LeaveRequestDetail>(`/api/leave/requests/${id}`),

  createRequest: (data: CreateLeaveRequest) =>
    api.post<LeaveRequestDetail>('/api/leave/requests', data),

  updateRequest: (id: string, data: CreateLeaveRequest) =>
    api.put<LeaveRequestDetail>(`/api/leave/requests/${id}`, data),

  submitRequest: (id: string) =>
    api.post<LeaveRequestDetail>(`/api/leave/requests/${id}/submit`),

  cancelRequest: (id: string, reason?: string) =>
    api.post<LeaveRequestDetail>(`/api/leave/requests/${id}/cancel`, { reason }),

  // Holidays
  getHolidays: (year?: number) => {
    const qs = year ? `?year=${year}` : '';
    return api.get<PublicHoliday[]>(`/api/leave/holidays${qs}`);
  },

  // Working days
  calculateWorkingDays: (data: {
    startDate: string; endDate: string;
    startDateIsHalfDay: boolean; startDateHalfDayPeriod?: string | null;
    endDateIsHalfDay: boolean; endDateHalfDayPeriod?: string | null;
    employeeId?: string;
  }) => api.post<WorkingDaysResponse>('/api/leave/working-days', data),

  // Overlap check
  checkOverlap: (startDate: string, endDate: string, employeeId?: string) =>
    api.post<LeaveOverlapWarning>(`/api/leave/check-overlap?startDate=${startDate}&endDate=${endDate}${employeeId ? `&employeeId=${employeeId}` : ''}`),

  // Team (Manager / HR)
  getTeamPendingRequests: () =>
    api.get<LeaveRequestListItem[]>('/api/leave/team/requests'),

  getTeamMembers: (departmentId?: number, officeLocationId?: number, managerId?: string) => {
    const params = new URLSearchParams();
    if (departmentId) params.set('departmentId', departmentId.toString());
    if (officeLocationId) params.set('officeLocationId', officeLocationId.toString());
    if (managerId) params.set('managerId', managerId);
    const qs = params.toString();
    return api.get<TeamMember[]>(`/api/leave/team/members${qs ? '?' + qs : ''}`);
  },

  getEmployeeBalance: (employeeId: string) =>
    api.get<LeaveBalance[]>(`/api/leave/team/balance/${employeeId}`),

  getTeamCalendar: (startDate: string, endDate: string, departmentId?: number, officeLocationId?: number, managerId?: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    if (departmentId) params.set('departmentId', departmentId.toString());
    if (officeLocationId) params.set('officeLocationId', officeLocationId.toString());
    if (managerId) params.set('managerId', managerId);
    return api.get<TeamCalendarEntry[]>(`/api/leave/team/calendar?${params}`);
  },

  getTeamBalances: () =>
    api.get<LeaveBalanceReportItem[]>('/api/leave/team/balances'),

  approveRequest: (id: string, comments?: string) =>
    api.post<LeaveRequestDetail>(`/api/leave/requests/${id}/approve`, { comments }),

  rejectRequest: (id: string, comments?: string) =>
    api.post<LeaveRequestDetail>(`/api/leave/requests/${id}/reject`, { comments }),

  revokeRequest: (id: string, comments?: string) =>
    api.post<LeaveRequestDetail>(`/api/leave/requests/${id}/revoke`, { comments }),

  // Leave Types (any authenticated user)
  getLeaveTypes: () =>
    api.get<LeaveType[]>('/api/leave/types'),

  getAllLeaveTypes: () =>
    api.get<LeaveType[]>('/api/leave/types/all'),

  // Reports
  getBalanceReport: (departmentId?: number, officeLocationId?: number, year?: number) => {
    const params = new URLSearchParams();
    if (departmentId) params.set('departmentId', departmentId.toString());
    if (officeLocationId) params.set('officeLocationId', officeLocationId.toString());
    if (year) params.set('year', year.toString());
    const qs = params.toString();
    return api.get<LeaveBalanceReportItem[]>(`/api/leave/reports/balances${qs ? '?' + qs : ''}`);
  },

  getUsageReport: (startDate?: string, endDate?: string, departmentId?: number, officeLocationId?: number, leaveTypeCode?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (departmentId) params.set('departmentId', departmentId.toString());
    if (officeLocationId) params.set('officeLocationId', officeLocationId.toString());
    if (leaveTypeCode) params.set('leaveTypeCode', leaveTypeCode);
    const qs = params.toString();
    return api.get<LeaveUsageReportItem[]>(`/api/leave/reports/usage${qs ? '?' + qs : ''}`);
  },

  // Approval Delegation — moved to /api/approvals/delegations (managed on Approvals Inbox page)
};
